import { useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useLocationApi } from "@/hooks/apiHooks/useLocationApi";
import { ROUTES } from "@routing/routes";
import { Button, Icon } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import {
    LocationStatus,
    RejectionReasonCategory,
    REJECTION_REASON_CATEGORY_LABEL,
} from "@models/location";
import type { Location } from "@models/organization";
import { STATUS_NOT_EDITABLE_MESSAGE } from "../_helpers/editLocation";
import { canEditAddress } from "../_helpers/locationForm";
import { LocationCertificates } from "./LocationCertificates";
import { LocationStatusBadge } from "./LocationStatusBadge";
import { NewCertBadge } from "./NewCertBadge";
import { RejectLocationDialog } from "./RejectLocationDialog";

interface LocationRowProps {
    location: Location;
    // Re-fetch the org detail after a location's status changes.
    onRejected: () => void;
    // Re-fetch the org detail after its re-uploaded certificate is
    // acknowledged (E12-S5 / #240) — that is what clears the badge.
    onAcknowledged: () => unknown;
    // Opens the Edit Location drawer (C6) for this row.
    onEdit: () => void;
    // The page holds Edit while a write is out or its newest read of the
    // client has not succeeded (the copy could be stale).
    isEditHeld: boolean;
    // What the last Edit Location save of this row did.
    outcome?: ReactNode;
}

export const LocationRow = ({
    location,
    onRejected,
    onAcknowledged,
    onEdit,
    isEditHeld,
    outcome,
}: LocationRowProps) => {
    const { rejectLocation } = useLocationApi();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    // A successful reject removes this row's Reject button (only a Pending or
    // pending-payment location has one), so the dialog hands focus back to the
    // row instead.
    const rowRef = useRef<HTMLLIElement>(null);

    const handleReject = async (
        category: RejectionReasonCategory,
        notes: string | undefined,
    ) => {
        setIsRejecting(true);
        try {
            await rejectLocation(location.id, {
                rejectionReasonCategory: category,
                rejectionReasonNotes: notes,
            });
            setIsDialogOpen(false);
            onRejected();
        } catch {
            // useApi already surfaced the error toast; keep the dialog open so
            // the admin can retry or cancel.
        } finally {
            setIsRejecting(false);
        }
    };

    const isPending = location.status === LocationStatus.Pending;
    // #434: passed review, waiting for its activation charge. The API lets an
    // admin reject it; approving it again is the org-level Approve control.
    const isPendingPayment =
        location.status === LocationStatus.PendingPayment;
    const isRejected = location.status === LocationStatus.Rejected;
    const rejectionCategory =
        location.rejectionReasonCategory ?? RejectionReasonCategory.None;
    // A status the API refuses to edit renders Edit disabled, with the reason
    // under the row, linked through aria-describedby (#442, #452).
    const isEditable = canEditAddress(location.status);
    const editReasonId = useId();

    return (
        <li ref={rowRef} tabIndex={-1} className="py-2">
            <div className="flex items-center justify-between gap-3">
                <span>{location.name}</span>
                <div className="flex items-center gap-2">
                    {location.status !== undefined && (
                        <LocationStatusBadge status={location.status} />
                    )}
                    {location.hasPendingCertReview && <NewCertBadge />}
                    <Button
                        color={ComponentColor.Primary}
                        outline
                        size={ComponentSize.SM}
                        aria-label={`Edit ${location.name}`}
                        aria-describedby={
                            isEditable ? undefined : editReasonId
                        }
                        disabled={!isEditable || isEditHeld}
                        onClick={onEdit}
                    >
                        <Icon name="edit" />
                        Edit
                    </Button>
                    {(isPending || isPendingPayment) && (
                        <Button
                            color={ComponentColor.Error}
                            size={ComponentSize.SM}
                            disabled={isRejecting}
                            onClick={() => setIsDialogOpen(true)}
                        >
                            Reject
                        </Button>
                    )}
                </div>
            </div>

            {!isEditable && (
                <p id={editReasonId} className="mt-1 text-sm text-neutral/70">
                    {STATUS_NOT_EDITABLE_MESSAGE}
                </p>
            )}

            {outcome}

            {/* The admin detail carries no charge outcome, so this row cannot
                tell an unconfirmed charge (money MAY have moved) from a
                decline: it never invites "approve again" unconditionally. */}
            {isPendingPayment && (
                <p className="mt-1 text-sm text-neutral/70">
                    Passed review; waiting for payment. Approved automatically
                    when the customer&apos;s card or the next membership renewal
                    goes through. If its charge is unconfirmed, resolve the
                    unconfirmed charge first in{" "}
                    <Link
                        to={ROUTES.ADMIN_UNCONFIRMED_CHARGES}
                        className="link link-primary"
                    >
                        Unconfirmed charges
                    </Link>
                    ; otherwise approve again to retry the charge.
                </p>
            )}

            {isRejected &&
                rejectionCategory !== RejectionReasonCategory.None && (
                <p className="mt-1 text-sm text-neutral/70">
                    Rejected:{" "}
                    {REJECTION_REASON_CATEGORY_LABEL[rejectionCategory]}
                    {location.rejectionReasonNotes
                        ? ` — ${location.rejectionReasonNotes}`
                        : ""}
                </p>
            )}

            <LocationCertificates
                location={location}
                onAcknowledged={onAcknowledged}
            />

            {isDialogOpen && (
                <RejectLocationDialog
                    locationName={location.name}
                    isProcessing={isRejecting}
                    onConfirm={handleReject}
                    onClose={() => setIsDialogOpen(false)}
                    returnFocusRef={rowRef}
                />
            )}
        </li>
    );
};
